import { type FC } from 'react';

import NotificationModal from '../../../utils/modal';

interface IDuplicateApplicationModalProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** The server's error message (or a friendly fallback), shown verbatim. */
	message: string;
	email: string;
	phoneNumber: string;
	/** Close and return the user to the form (focuses the email field). */
	onEdit: () => void;
	onContactSupport?: () => void;
}

const SUPPORT_EMAIL = 'support@ielect.app';

/**
 * Hard-block dialog shown when the duplicate check reports an existing application for this
 * election. Framed as "you've already applied" guidance rather than a raw error — the user cannot
 * advance, but can edit their details or reach support. Built on `NotificationModal` (react-bootstrap)
 * so it inherits portal-to-body + scroll-lock + Escape/focus handling — the create flow's blurred,
 * low-z-index wrapper would otherwise trap a self-rendered overlay under the header.
 */
const DuplicateApplicationModal: FC<IDuplicateApplicationModalProps> = ({
	isOpen,
	setIsOpen,
	message,
	email,
	phoneNumber,
	onEdit,
	onContactSupport,
}) => (
	<NotificationModal
		openModal={isOpen}
		setOpenModal={setIsOpen}
		title='You’ve already applied'
		closeFunction={onEdit}
	>
		<div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 4px' }}>
			<p
				style={{
					margin: 0,
					fontFamily: 'var(--font-family-regular)',
					fontSize: 'var(--text-body-size)',
					color: 'var(--color-text-secondary)',
					lineHeight: 1.5,
				}}
			>
				Our records show an application already exists for this election using your email or phone number. You don’t need to apply
				again — if it was you, we’ll be in touch about the next steps.
			</p>

			<div
				style={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: '10px',
					padding: '12px 14px',
					borderRadius: '10px',
					backgroundColor: 'var(--color-error-bg)',
					color: 'var(--color-error-text)',
					fontFamily: 'var(--font-family-medium)',
					fontSize: '13px',
					lineHeight: 1.5,
				}}
			>
				<span aria-hidden>⚠</span>
				<span>{message}</span>
			</div>

			{(email || phoneNumber) && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
						fontFamily: 'var(--font-family-regular)',
						fontSize: 'var(--text-body-sm-size)',
						color: 'var(--color-text-tertiary)',
					}}
				>
					{email && (
						<span>
							<strong style={{ fontFamily: 'var(--font-family-semibold)', color: 'var(--color-text-secondary)' }}>Email:</strong>{' '}
							{email}
						</span>
					)}
					{phoneNumber && (
						<span>
							<strong style={{ fontFamily: 'var(--font-family-semibold)', color: 'var(--color-text-secondary)' }}>Phone:</strong>{' '}
							{phoneNumber}
						</span>
					)}
				</div>
			)}

			<p
				style={{
					margin: 0,
					fontFamily: 'var(--font-family-regular)',
					fontSize: 'var(--text-body-sm-size)',
					color: 'var(--color-text-tertiary)',
					lineHeight: 1.55,
				}}
			>
				Think this is a mistake? Edit your details and try again, or reach out and we’ll help sort it out.
			</p>

			<div
				style={{
					display: 'flex',
					gap: '12px',
					marginTop: '8px',
					justifyContent: 'flex-end',
					flexWrap: 'wrap',
				}}
			>
				<a
					href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Volunteer application — already exists')}`}
					onClick={() => {
						setIsOpen(false);
						onContactSupport?.();
					}}
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						minHeight: '44px',
						padding: '12px 18px',
						border: '1px solid var(--color-border-strong)',
						borderRadius: '10px',
						backgroundColor: 'transparent',
						color: 'var(--color-text-primary)',
						fontFamily: 'var(--font-family-semibold)',
						fontSize: '14px',
						textDecoration: 'none',
						cursor: 'pointer',
					}}
				>
					Contact support
				</a>
				<button
					type='button'
					onClick={() => {
						setIsOpen(false);
						onEdit();
					}}
					style={{
						minHeight: '44px',
						padding: '12px 18px',
						border: 'none',
						borderRadius: '10px',
						backgroundColor: 'var(--color-brand-primary)',
						color: '#fff',
						fontFamily: 'var(--font-family-semibold)',
						fontSize: '14px',
						cursor: 'pointer',
					}}
				>
					Edit my details
				</button>
			</div>
		</div>
	</NotificationModal>
);

export default DuplicateApplicationModal;
